/**
 * Sync Service - Orchestration of data synchronization
 * Handles migration from localStorage to Supabase and sync operations
 */

import { supabase } from '../../supabaseClient';
import { taskService } from './taskService';
import { projectService } from './projectService';
import { tagService } from './tagService';
import { profileService } from './profileService';
import {
  getTasks,
  getProjects,
  getTags,
  setTasks,
  setProjects,
  setTags,
  isMigrationDone,
  markMigrationDone,
  setMigrationBackup,
  downloadJSON
} from '../utils/localStorage';
import { syncStatus } from '../utils/syncStatus';

/**
 * Fetch all data from Supabase
 */
export async function fetchAllData() {
  try {
    const [tasksResult, projectsResult, tagsResult, profileResult] = await Promise.all([
      taskService.fetchAll(),
      projectService.fetchAll(),
      tagService.fetchAll(),
      profileService.fetch()
    ]);

    // Check if any request failed
    if (!tasksResult.success || !projectsResult.success || !tagsResult.success) {
      throw new Error('Failed to fetch some data from Supabase');
    }

    return {
      success: true,
      data: {
        tasks: tasksResult.data,
        projects: projectsResult.data,
        tags: tagsResult.data,
        profile: profileResult.data
      }
    };
  } catch (error) {
    console.error('[syncService.fetchAllData] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Migrate localStorage data to Supabase
 */
export async function migrateLocalStorageToSupabase(userId) {
  // Check if already migrated
  if (isMigrationDone(userId)) {
    console.log('[Migration] Already migrated for user:', userId);
    return { success: true, alreadyMigrated: true };
  }

  try {
    // 1. Backup localStorage to JSON
    const backup = {
      tasks: localStorage.getItem('tasks'),
      projects: localStorage.getItem('projects'),
      tags: localStorage.getItem('tags'),
      timestamp: new Date().toISOString()
    };
    setMigrationBackup(backup);

    // 2. Parse local data
    const localTags = getTags();
    const localProjects = getProjects();
    const localTasks = getTasks();

    // 3. Check if Supabase already has data (skip if yes)
    const { data: existingProjects } = await supabase
      .from('projects')
      .select('id')
      .limit(1);

    if (existingProjects && existingProjects.length > 0) {
      console.log('[Migration] Supabase already has data, skipping migration');
      markMigrationDone(userId, 'skipped');
      return { success: true, skipped: true };
    }

    // 4. Migrate in order: tags → projects → tasks → subtasks
    const tagIdMap = {};
    const projectIdMap = {};

    // Migrate Tags
    console.log(`[Migration] Migrating ${localTags.length} tags...`);
    for (const tag of localTags) {
      const { data, error } = await supabase
        .from('tags')
        .insert([{
          id: isValidUUID(tag.id) ? tag.id : undefined,
          user_id: userId,
          name: tag.name,
          color: tag.color
        }])
        .select()
        .single();

      if (error) {
        console.error('[Migration] Tag error:', error);
        throw error;
      }
      tagIdMap[tag.id] = data.id;
    }

    // Migrate Projects
    console.log(`[Migration] Migrating ${localProjects.length} projects...`);
    for (const project of localProjects) {
      if (project.id === 'default' || project.isDefault) {
        // Get the default project created by trigger
        const { data } = await supabase
          .from('projects')
          .select('id')
          .eq('owner_id', userId)
          .eq('is_default', true)
          .single();

        if (data) {
          projectIdMap[project.id] = data.id;
        }
      } else {
        const { data, error } = await supabase
          .from('projects')
          .insert([{
            id: isValidUUID(project.id) ? project.id : undefined,
            owner_id: userId,
            name: project.name,
            color: project.color || '#3b82f6',
            is_default: false
          }])
          .select()
          .single();

        if (error) {
          console.error('[Migration] Project error:', error);
          throw error;
        }
        projectIdMap[project.id] = data.id;
      }
    }

    // Migrate Tasks + Subtasks
    console.log(`[Migration] Migrating ${localTasks.length} tasks...`);
    for (const task of localTasks) {
      const mappedProjectId = projectIdMap[task.projectId];
      if (!mappedProjectId) {
        console.warn('[Migration] No project mapping for task:', task.id);
        continue;
      }

      const { data: taskData, error: taskError } = await supabase
        .from('tasks')
        .insert([{
          id: isValidUUID(task.id) ? task.id : undefined,
          project_id: mappedProjectId,
          title: task.title,
          description: task.description,
          priority: task.priority || 'medium',
          due_date: task.dueDate,
          end_date: task.endDate,
          start_time: task.startTime,
          duration: task.duration,
          is_multi_day: task.isMultiDay || false,
          completed: task.completed || false,
          completed_at: task.completedAt,
          points_awarded: task.pointsAwarded || false,
          points_deducted: task.pointsDeducted || false,
          points_value: task.pointsValue || 10,
          flex_mode: task.flexMode || false,
          anti_overlap: task.antiOverlap || false
        }])
        .select()
        .single();

      if (taskError) {
        console.error('[Migration] Task error:', taskError);
        throw taskError;
      }

      // Migrate Subtasks
      if (task.subTasks && task.subTasks.length > 0) {
        const subtasksToInsert = task.subTasks.map((st, index) => ({
          id: isValidUUID(st.id) ? st.id : undefined,
          task_id: taskData.id,
          title: st.title,
          due_date: st.dueDate,
          end_date: st.endDate,
          time: st.time,
          duration: st.duration,
          completed: st.completed || false,
          is_multi_day: st.isMultiDay || false,
          position: index
        }));

        const { error: subtasksError } = await supabase
          .from('subtasks')
          .insert(subtasksToInsert);

        if (subtasksError) {
          console.error('[Migration] Subtasks error:', subtasksError);
          throw subtasksError;
        }
      }

      // Migrate Task Tags
      if (task.tags && task.tags.length > 0) {
        const taskTagsToInsert = task.tags
          .filter(tagId => tagIdMap[tagId]) // Only include tags that were migrated
          .map(tagId => ({
            task_id: taskData.id,
            tag_id: tagIdMap[tagId]
          }));

        if (taskTagsToInsert.length > 0) {
          const { error: tagsError } = await supabase
            .from('task_tags')
            .insert(taskTagsToInsert);

          if (tagsError) {
            console.error('[Migration] Task tags error:', tagsError);
            throw tagsError;
          }
        }
      }
    }

    // 5. Mark migration as done
    markMigrationDone(userId, 'success');
    console.log('[Migration] Migration successful!');

    return { success: true, migrated: true };
  } catch (error) {
    console.error('[Migration] Migration failed:', error);

    // Fallback: keep localStorage + export backup
    const backup = localStorage.getItem('migration_backup');
    if (backup) {
      downloadJSON(backup, `efficacitas_backup_${userId}_${Date.now()}.json`);
    }

    return { success: false, error: error.message };
  }
}

/**
 * Merge remote data with local data (last-write-wins)
 */
export function mergeWithLocalData(remoteData, setState) {
  const localTasks = getTasks();
  const localProjects = getProjects();
  const localTags = getTags();
  const pending = syncStatus.getPending();

  // Merge Tasks
  const mergedTasks = mergeTasks(localTasks, remoteData.tasks, pending);
  setState.setTasks(mergedTasks);
  setTasks(mergedTasks);

  // Merge Projects
  const mergedProjects = mergeProjects(localProjects, remoteData.projects, pending);
  setState.setProjects(mergedProjects);
  setProjects(mergedProjects);

  // Merge Tags
  const mergedTags = mergeTags(localTags, remoteData.tags, pending);
  setState.setTags(mergedTags);
  setTags(mergedTags);
}

/**
 * Merge tasks with last-write-wins strategy
 */
function mergeTasks(localTasks, remoteTasks, pending) {
  const merged = [];
  const processedIds = new Set();

  // Process remote tasks
  for (const remoteTask of remoteTasks) {
    const localTask = localTasks.find(t => t.id === remoteTask.id);

    if (!localTask) {
      merged.push(remoteTask);
    } else {
      // Compare timestamps (last-write-wins)
      const remoteTime = new Date(remoteTask.updatedAt || remoteTask.createdAt);
      const localTime = new Date(localTask.updatedAt || localTask.createdAt);

      merged.push(remoteTime >= localTime ? remoteTask : localTask);
    }

    processedIds.add(remoteTask.id);
  }

  // Process local-only tasks (temp-* or pending)
  for (const localTask of localTasks) {
    if (processedIds.has(localTask.id)) continue;

    if (localTask.id.startsWith('temp-') || pending[localTask.id]) {
      merged.push(localTask);
    }
  }

  return merged;
}

/**
 * Merge projects with last-write-wins strategy
 */
function mergeProjects(localProjects, remoteProjects, pending) {
  const merged = [];
  const processedIds = new Set();

  for (const remoteProject of remoteProjects) {
    const localProject = localProjects.find(p => p.id === remoteProject.id);

    if (!localProject) {
      merged.push(remoteProject);
    } else {
      const remoteTime = new Date(remoteProject.updatedAt || remoteProject.createdAt);
      const localTime = new Date(localProject.updatedAt || localProject.createdAt);

      merged.push(remoteTime >= localTime ? remoteProject : localProject);
    }

    processedIds.add(remoteProject.id);
  }

  for (const localProject of localProjects) {
    if (processedIds.has(localProject.id)) continue;

    if (localProject.id.startsWith('temp-') || pending[localProject.id]) {
      merged.push(localProject);
    }
  }

  return merged;
}

/**
 * Merge tags with last-write-wins strategy
 */
function mergeTags(localTags, remoteTags, pending) {
  const merged = [];
  const processedIds = new Set();

  for (const remoteTag of remoteTags) {
    const localTag = localTags.find(t => t.id === remoteTag.id);

    if (!localTag) {
      merged.push(remoteTag);
    } else {
      const remoteTime = new Date(remoteTag.updatedAt || remoteTag.createdAt);
      const localTime = new Date(localTag.updatedAt || localTag.createdAt);

      merged.push(remoteTime >= localTime ? remoteTag : localTag);
    }

    processedIds.add(remoteTag.id);
  }

  for (const localTag of localTags) {
    if (processedIds.has(localTag.id)) continue;

    if (localTag.id.startsWith('temp-') || pending[localTag.id]) {
      merged.push(localTag);
    }
  }

  return merged;
}

/**
 * Validate UUID format
 */
function isValidUUID(str) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

export const syncService = {
  fetchAllData,
  migrateLocalStorageToSupabase,
  mergeWithLocalData
};
